import React, { useEffect, useMemo, useState } from 'react'

import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'

import { Search } from 'components'
import { CompanyAvatar } from 'components/display/CompanyAvatar/CompanyAvatar'
import { useSnackbar } from 'contexts'
import { useCompaniesQuery, useUpdateInternalUser } from 'hooks/api'
import { getErrorMessage } from 'utils'
import { listedCompanies } from 'utils/listedCompany'

interface AssignCompaniesModalProps {
  user: {
    id: string
    email: string
    role?: { id: string; name: string }
    companies?: Array<{ id: string; name: string; logo: string | null }>
    active?: boolean
  }
  open: boolean
  onClose: () => void
  onUpdated?: () => void
}

/**
 * Assign a Vera staffer to one or more companies. Mirrors AssignUsersModal:
 * a Search box + a scrollable multi-select checklist (reusing the shared
 * `Search` + `CompanyAvatar` components), NOT a bare MUI select.
 */
export const AssignCompaniesModal = ({ user, open, onClose, onUpdated }: AssignCompaniesModalProps) => {
  const { open: notification } = useSnackbar()
  const { mutate: updateUser, isPending } = useUpdateInternalUser()
  const { data: companiesData, isLoading } = useCompaniesQuery({ limit: 1000 })

  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Snapshot of the companies assigned when the modal opened. Used to float the
  // current assignments to the top of the list (easy to review/remove). It is the
  // ON-OPEN set, NOT the live selection, so the list does NOT reorder under the
  // cursor as you check/uncheck — only re-sorting on the next open.
  const [assignedOnOpen, setAssignedOnOpen] = useState<Set<string>>(new Set())

  // Pre-select the user's current companies each time the modal opens.
  useEffect(() => {
    if (open) {
      const ids = new Set((user.companies ?? []).map((c) => c.id))
      setSelectedIds(ids)
      setAssignedOnOpen(ids)
      setSearch('')
    }
  }, [open, user.companies])

  // `listedCompanies` unwraps each `/companies/admin` row to its company and drops any
  // without an id (VRG-706). Sort: assigned-first (the on-open snapshot, stable for the
  // session), then A→Z.
  const companies = useMemo(() => {
    const list = listedCompanies(companiesData)
    return list.sort((a, b) => {
      const aRank = assignedOnOpen.has(a.id) ? 0 : 1
      const bRank = assignedOnOpen.has(b.id) ? 0 : 1
      if (aRank !== bRank) return aRank - bRank
      return (a.name ?? '').localeCompare(b.name ?? '')
    })
  }, [companiesData, assignedOnOpen])

  const filtered = useMemo(() => {
    if (!search.trim()) return companies
    const q = search.toLowerCase()
    return companies.filter((c) => (c.name ?? '').toLowerCase().includes(q))
  }, [companies, search])

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleSave = () => {
    if (selectedIds.size === 0) {
      notification({ type: 'error', title: 'Please select at least one company' })
      return
    }
    updateUser(
      {
        userId: user.id,
        role_name: user.role?.name,
        company_ids: Array.from(selectedIds),
        active: user.active,
      },
      {
        onSuccess: () => {
          onUpdated?.()
          onClose()
        },
        onError: (error) => {
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      },
    )
  }

  return (
    <Modal
      title="Assign companies"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isPending} disabled={selectedIds.size === 0 || isPending}>
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search companies"
          fullWidth
          debounce={false}
        />

        <Box
          sx={(theme) => ({
            maxHeight: 360,
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                Loading companies...
              </Typography>
            </Stack>
          ) : filtered.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body3" color="text.secondary">
                {companies.length === 0 ? 'No companies found.' : 'No companies match your search.'}
              </Typography>
            </Stack>
          ) : (
            filtered.map((company, index) => (
              <Stack
                key={company.id}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => toggle(company.id)}
                sx={(theme) => ({
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderBottom:
                    index < filtered.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                })}
              >
                <Checkbox
                  checked={selectedIds.has(company.id)}
                  onChange={() => toggle(company.id)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <CompanyAvatar logo={company.logo} name={company.name} size={32} />
                <Typography variant="body2" noWrap>
                  {company.name}
                </Typography>
              </Stack>
            ))
          )}
        </Box>
      </Stack>
    </Modal>
  )
}
